<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset}" ref="observer">
    <artisio-modal :visible="show" :title="computedTitle" body-class="general-contract-modal-body p-0" size="xl"
                   scrollable hide-footer
                   :content-in-form="false" @hidden="onHidden" modal-class="general-contract-modal mobile-full-screen">
      <spinner :show="loading"/>
      <div class="data-wrapper" v-if="!loading">
        <artisio-e-signature ref="artisioESignature" @submit="onTouchpadSubmit"/>
        <div class="pdf-header d-flex justify-content-between">
          <div class="p-2 header-actions-wrapper w-100">
            <div class="mr-auto">

              <template v-if="enableESignature">

                <template v-if="isDocusignEnabled">
                  <submit-button @click="onESignatureSend" :disabled="disableActions" class="mr-2"
                                 v-if="contract_data.status === 'draft'" :showLoader="buttonLoading">
                    <font-awesome-icon icon="file-signature"/>
                    {{ $t('Send by Docusign') }}
                  </submit-button>
                  <b-button variant="primary" @click="onESignatureResend" :disabled="disableActions" class="mr-2"
                            v-else-if="contract_data.status === 'sent' && isThirdPartyPlatform(contract_data)">
                    <font-awesome-icon icon="file-signature"/>
                    {{ $t('Resend by Docusign') }}
                  </b-button>
                </template>

                <template v-else-if="['receipt_contract', 'pre_receipt_contract'].includes(fileType)">
                  <submit-button @click="onESignatureSend" :disabled="disableActions" class="mr-2"
                                 v-if="contract_data.status === 'draft'" :showLoader="buttonLoading">
                    <font-awesome-icon icon="file-signature"/>
                    {{ $t('Send Request Signature Notification') }}
                  </submit-button>
                </template>

              </template>

              <submit-button @click="onTouchpadClick" :disabled="disableActions" class="mr-2"
                             :show-loader="buttonLoading" v-if="!hideActions">
                <font-awesome-icon icon="tablet-alt"/>
                {{ $t('Sign by Touchpad') }}
              </submit-button>

              <submit-button @click="$refs.generalContractDocumentInput.click()" class="mr-2"
                             :disabled="disableActions" :show-loader="buttonLoading" v-if="!hideActions">
                <font-awesome-icon icon="check"/>
                {{ $t('Sign Manually') }}
              </submit-button>

              <submit-button @click="viewInContractsClick"
                             :disabled="disableActions" :show-loader="buttonLoading"
                             v-if="isDocusignEnabled && hasContractCustomer">
                <font-awesome-icon icon="eye"/>
                <template v-if="['receipt_contract', 'pre_receipt_contract'].includes(fileType)">
                  {{ $t('View Receipt Contracts') }}
                </template>
                <template v-else>
                  {{ $t('View Customer Contracts') }}
                </template>

              </submit-button>

              <input :multiple="false" @change="onInputChange"
                     accept=".pdf" type="file" class="d-none"
                     ref="generalContractDocumentInput"
                     id="template-uploader-input"/>
            </div>

            <div>
              <submit-button variant="danger" @click="onRegenerateClick"
                             :disabled="disableActionByStaff" :show-loader="buttonLoading">
                <font-awesome-icon icon="undo"/>
                {{ $t('Regenerate') }}
              </submit-button>
              <submit-button @click="onSignByStaffClick" class="ml-2" :disabled="disableActionByStaff"
                             :show-loader="buttonLoading" v-if="!hideActions">
                <font-awesome-icon icon="file-signature"/>
                {{ $t('Sign by Staff') }}
              </submit-button>
            </div>

          </div>
        </div>
        <div v-if="pdf_url && isIOS" class="pdf-open-link-wrapper">
          <b-button variant="primary" @click="openPdfOnIOS">
            <font-awesome-icon icon="file-pdf"/>
            {{ $t('Open PDF') }}
          </b-button>
        </div>
        <iframe v-else-if="pdf_url" class="pdf-iframe" :src="pdf_url"/>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "./../artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "@/core/components/common/Spinner";
  import {
    SOURCE_GENERAL_CONTRACT_MODAL,
    SOURCE_INVOICES_TABLE,
    SOURCE_RETURN_NOTES_TABLE,
    SOURCE_TRANSFER_NOTES_TABLE
  } from "@/modules/constants";
  import ArtisioESignature from "@/core/components/e-signature/ArtisioESignature";
  import ContractService from "@/modules/contracts/ContractService";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import SubmitButton from "@/core/components/common/SubmitButton.vue";

  const {mapActions: invoicesMapActions} = createNamespacedHelpers('invoices');
  const {mapActions: returnNotesMapActions} = createNamespacedHelpers('returnNotes');
  const {mapActions: transferNotesMapActions} = createNamespacedHelpers('transferNotes');
  const {mapActions: collectionsMapActions} = createNamespacedHelpers('collections');
  const {mapActions: contractsMapActions} = createNamespacedHelpers('contracts');
  const {mapActions: mapActionsGlobal, mapState} = createNamespacedHelpers('global');

  export default {
    name: "GeneralContractModal",
    components: {SubmitButton, ArtisioESignature, Spinner, ArtisioModal},
    data() {
      return {
        showConfirmation: true,
        buttonLoading: false,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.generalContractModal,
        show: state => state.generalContractModal.show,
        fileType: state => state.generalContractModal.fileType,
        objectName: state => state.generalContractModal.objectName,
        enableESignature: state => state.generalContractModal.enableESignature,
        object: state => state.generalContractModal.object,
        pdf_url: state => state.generalContractModal.pdf_url,
        loading: state => state.generalContractModal.loading,
        refreshOnSubmit: state => state.generalContractModal.refreshOnSubmit,
        modalTitle: state => state.generalContractModal.modalTitle,
        contract_data: state => state.generalContractModal.contract_data,
        isDocusignEnabled: state => state.isDocusignEnabled,
      }),
      computedTitle() {
        if (this.modalTitle) {
          return this.modalTitle
        }
        return this.$i18n.t('Contract')
      },
      disableActions() {
        return false
      },
      disableActionByStaff() {
        return false
      },
      hideActions() {
        return this.isDocusignEnabled && this.contract_data && this.contract_data.status !== 'draft';
      },
      hasContractCustomer() {
        return this.fileType && this.contract_data && !['transfer_note_contract'].includes(this.fileType)
      },
      isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      },
    },
    methods: {
      openPdfOnIOS() {
        window.open(this.pdf_url, '_blank');
      },
      ...contractsMapActions([
        'showContractSendModal',
      ]),
      ...mapActionsGlobal(['hideGeneralContractModal', 'getGeneralContractModalData']),
      ...returnNotesMapActions({
        getReturnNotesTableData: 'getTableData'
      }),
      ...transferNotesMapActions({
        getTransferNotesTableData: 'getTableData'
      }),
      ...invoicesMapActions({
        getInvoicesTableData: 'getTableData'
      }),
      ...collectionsMapActions([
        'showCollectionsModal',
      ]),
      onHidden() {
        this.hideGeneralContractModal();
      },
      toggleButtonLoader() {
        this.buttonLoading = !this.buttonLoading;
      },
      async onESignatureSend() {
        this.toggleButtonLoader()
        if (!(await this.handleReservationProposals())) {
          this.toggleButtonLoader()
          return
        }

        await ContractService.onFileSend(this, this.contract_data, SOURCE_GENERAL_CONTRACT_MODAL, () => {
          this.afterSubmit()
        })
        this.toggleButtonLoader()
      },
      async onESignatureResend() {
        this.toggleButtonLoader()
        await ContractService.onFileResend(this, this.contract_data)
        this.toggleButtonLoader()
      },
      onTouchpadClick() {
        if (this.$refs.artisioESignature) {
          this.$refs.artisioESignature.open();
        }
      },
      async onTouchpadSubmit(data) {
        this.toggleButtonLoader()
        if (!(await this.handleReservationProposals())) {
          this.toggleButtonLoader()
          return
        }
        let response = await ContractService.contractSubmitSignature(this.object.uuid, {base64: data}, this.objectName, this.fileType)
        if (response.success) {
          this.$toast('success', this.$i18n.t('Contract was successfully signed'));
          this.afterSubmit()
        }
        this.toggleButtonLoader()
      },
      async onInputChange($event) {
        const files = $event.target.files;
        if (files.length === 0) {
          return false;
        }

        let formData = new FormData();
        let error = null
        files.forEach(item => {
          if (item.type !== 'application/pdf') {
            error = this.$i18n.t('Only PDF files are accepted')
          } else {
            formData.append('files', item)
          }
        })

        if (error) {
          this.$errorDialog(error)
          return
        }

        formData.append('contract_data', JSON.stringify({
          "status": "signed",
          "object_name": this.objectName,
          "object_uuid": this.object.uuid,
          "customer_uuid": this.object.customer_uuid,
          "type": this.fileType,
          "is_unique": true
        }))

        this.uploadingProgress = true;

        this.toggleButtonLoader()
        let response = await ContractService.uploadSignedPdfManually({
          formData, onUploadProgress: (progressEvent) => {
            this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          }
        });
        this.toggleButtonLoader()

        if (response.success) {
          this.$toast('success', this.$i18n.t('Contract was successfully uploaded'));
          this.afterSubmit()
        }

        this.$refs.generalContractDocumentInput.value = null;
      },
      async onSignByStaffClick() {
        this.toggleButtonLoader()
        let success = await this.$confirm(this.$i18n.t("Are you sure you want to sign following contract?"))
        if (!success) return;

        if (!(await this.handleReservationProposals())) {
          this.toggleButtonLoader()
          return
        }

        let response = await ContractService.contractSubmitStaffSignature(this.object.uuid, this.objectName, this.fileType)
        if (response.success) {
          this.$toast('success', this.$i18n.t('Contract was successfully signed by staff'));
          this.afterSubmit()
        }
        this.toggleButtonLoader()
      },
      async onRegenerateClick() {
        let success = await this.$confirm(
          this.$i18n.t("If you regenerate the contract, you might lose existing signature(s). Are you sure you want to proceed?"),
          null,
          null,
          null,
          this.$i18n.t('Please Confirm'),
          this.$i18n.t('Yes'),
          this.$i18n.t('No'),
          null,
          {
            label: this.$i18n.t("To confirm regeneration, type 'regenerate' in the text input field"),
            text: 'regenerate'
          }
        )
        if (!success) {
          return;
        }

        let handler = async (data = {}) => {
          this.toggleButtonLoader()
          let response = await ContractService.contractRegenerate(this.object.uuid, this.objectName, this.fileType, data)
          this.toggleButtonLoader()
          if (response.success) {
            this.$toast('success', this.$i18n.t('Contract was successfully regenerated'));
            this.afterSubmit()
          } else {
            this.$errorDialog(
              (response.body && response.body.non_field_errors && response.body.non_field_errors[0]) || this.$i18n.t('Something went wrong'),
              this.fileType === 'collection_note' ? this.$i18n.t('Collection Note not available') : this.$i18n.t('Error')
            )
          }
          return response
        }

        if (this.fileType === 'collection_note' || this.fileType === 'return_note_contract') {
          this.showCollectionsModal({
            customer_uuid: this.object.customer_uuid,
            source: this.fileType,
            handler: handler,
            title: this.fileType === 'collection_note' ? this.$i18n.t('Collection Note') : this.$i18n.t('Return Note')
          })
        } else {
          handler()
        }
      },
      async afterSubmit() {
        if (!this.refreshOnSubmit) {
          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getInvoicesTableData()
          } else if (this.modal.source === SOURCE_RETURN_NOTES_TABLE) {
            this.getReturnNotesTableData();
          } else if (this.modal.source === SOURCE_TRANSFER_NOTES_TABLE) {
            this.getTransferNotesTableData();
          }
          this.hideGeneralContractModal()
        } else {
          this.getGeneralContractModalData({
            object: this.object,
            fileType: this.fileType,
            objectName: this.objectName,
          });
        }
      },
      async handleReservationProposals() {
        if (this.fileType !== 'collection_note') {
          return true
        }

        const reservation_proposals = (await LotsService.getLatestReservationProposals({
          invoice_uuids: [this.object.uuid],
          suggestions: ['reoffer'],
        })).body
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) {
            return false
          }
        }
        return true
      },
      isThirdPartyPlatform(contract) {
        return ContractService.isThirdPartyPlatform(contract)
      },
      viewInContractsClick() {
        if (['receipt_contract', 'pre_receipt_contract'].includes(this.fileType)) {
          if (this.contract_data.object_uuid) {
            this.$router.push({
              name: 'receipt.contracts',
              params: {id: this.contract_data.object_uuid},
            });
            this.hideGeneralContractModal()
          }
        } else {
          if (this.contract_data && this.contract_data.customer_uuid) {
            this.$router.push({
              name: 'customers.contracts',
              params: {id: this.contract_data.customer_uuid},
            });
            this.hideGeneralContractModal()
          }
        }

      }
    },
  }
</script>

<style lang="scss" scoped>
  .general-contract-modal {
    .general-contract-modal-body {
      .data-wrapper {
        height: calc(100vh - 10em) !important;
        display: flex;
        justify-content: center;
        flex-direction: column;

        .pdf-iframe {
          width: 100%;
          height: 100%;
        }

        .pdf-open-link-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }

  .pdf-document {
    .items-table {
      td {
        &:first-child {
          overflow-wrap: anywhere;
        }
      }
    }
  }

  .header-actions-wrapper {
    display: flex;
  }

  .header-action-btn {
    flex: 2;
  }
</style>
